import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FmlxDropdown } from 'fmlx-common-ui';
import {
  GetContainersForExperimentInfo,
  getNewId,
  isNullOrUndefined,
} from 'utils/Global';
import { useRMSelector } from 'redux/hooks';
import { selectGlobalDataLoaded } from 'redux/selectors/StateSelectors';
import { hasModifyAccess } from 'utils/accessRights';
import ExperimentInfoRow from '../ExperimentInfo/ExperimentInfoRow';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { IExperimentScreenLot } from 'business/models/Models';
import { useExperimentContainerId } from 'redux/hooks/experiment';
import { Containers } from 'components/Experiment/constants';
import EContainerType from 'enums/EContainerType';
interface Props {
  expData: ExperimentDtoClient;
  selectedLot: IExperimentScreenLot;
  addLot: (lot: IExperimentScreenLot) => void;
  updateLot: (lot: IExperimentScreenLot) => void;
  setSelectedLot: (lot: IExperimentScreenLot) => void;
}
const AddScreenLot = ({
  expData,
  selectedLot,
  addLot,
  updateLot,
  setSelectedLot,
}: Props) => {
  const [containers, setContainers] = useState([]);
  const [lot, setLot] = useState<IExperimentScreenLot>(null);
  const _selectGlobalDataLoaded = useRMSelector(selectGlobalDataLoaded);
  const selectedContainerId = useExperimentContainerId();
  const isDefaultLot = selectedLot?.lotNumber === 0;
  const canModify = hasModifyAccess(expData?.accessRightName);

  useEffect(() => {
    if (selectedLot) {
      setLot(selectedLot);
    } else {
      setLot(getNewScreenLot(containers));
    }
  }, [selectedLot, _selectGlobalDataLoaded]);

  useEffect(() => {
    const _containers = GetContainersForExperimentInfo(expData?.info?.containerId, [
      EContainerType.ScreenContainer,
    ]);
    if (!_containers || _containers.length === 0) {
      return;
    }
    const filteredContainers = _containers.filter(
      (c) => c.rows === expData.containerRows && c.cols === expData.containerCols,
    );
    setContainers(filteredContainers);
    setLot(getNewScreenLot(filteredContainers));
  }, [_selectGlobalDataLoaded, selectedContainerId]);

  const onClick = useCallback(() => {
    if (!canModify) {
      return;
    }
    if (selectedLot) {
      updateLot(lot);
    } else {
      expData.info.screenLots.sort((a, b) => a.lotNumber - b.lotNumber);
      lot.lotNumber =
        (expData.info.screenLots[expData.info.screenLots.length - 1]?.lotNumber ?? 0) + 1;
      addLot(lot);
      const _lot = getNewScreenLot(containers);
      setLot({ ..._lot } as IExperimentScreenLot);
    }
  }, [canModify, lot, expData, addLot, setLot, updateLot]);

  const containerOptions = useMemo(
    () =>
      containers.map((container) => ({
        id: container.id,
        text: container.name,
        group: '',
        disabled: false,
      })),
    [containers],
  );

  const onChangeContainer = useCallback(
    (containerId: number) => {
      setLot({
        ...lot,
        containerId,
      } as IExperimentScreenLot);
    },
    [lot, setLot],
  );

  const onCurVolChange = useCallback(
    (e) => {
      setLot({
        ...lot,
        currentVol: parseFloat(e.target.value),
      } as IExperimentScreenLot);
    },
    [lot, setLot],
  );

  const onDisVolChange = useCallback(
    (e) => {
      setLot({
        ...lot,
        dispenseVol: parseFloat(e.target.value),
      } as IExperimentScreenLot);
    },
    [lot, setLot],
  );

  return (
    <div id="screenlot-add-container" className="screenLotContainer">
      <ExperimentInfoRow
        id="screenlot-add-comment-row"
        text="Comment"
        className="col-md-6"
      >
        <input
          id="screenlot-add-comment-input"
          className="inputFieldDetails"
          onChange={(e) =>
            setLot({ ...lot, comment: e.target.value } as IExperimentScreenLot)
          }
          value={lot?.comment}
          disabled={!canModify}
          placeholder="Input comments here"
        />
      </ExperimentInfoRow>
      <ExperimentInfoRow
        id="screenlot-add-container-row"
        text="Container"
        className="col-md-6"
      >
        {containers.length === 1 ? (
          <input
            className="inputFieldDetails"
            value={containers[0].name}
            disabled={true}
          />
        ) : (
          <div className="w-full">
            <FmlxDropdown
              id="screenlot-add-container-single"
              items={containerOptions}
              selectedItem={lot?.containerId}
              disabled={!canModify || isDefaultLot}
              onChange={onChangeContainer}
              size={FmlxDropdown.Size.SMALL}
              menuPosition={FmlxDropdown.MenuPosition.DEFAULT}
              menuProps={{ width: '100%' } as any}
            />
          </div>
        )}
      </ExperimentInfoRow>

      <div
        id="screenlot-add-volume-label"
        className="row plateRowColMargin notes my-[10px] mx-[30px]"
      >
        Volume
      </div>
      <div
        id="screenlot-add-volume-row"
        className="row plateRowColMargin my-[10px] mx-[30px]"
      >
        <div id="screenlot-add-dispense-label" className="col-md-3 p-[5px]">
          Dispense
        </div>
        <div className="col-md-3 padding0">
          <input
            id="screenlot-add-dispense-input"
            className="inputFieldDetails"
            onChange={onDisVolChange}
            value={lot?.dispenseVol}
            disabled={!canModify || isDefaultLot}
            type="number"
          />
        </div>
        <div id="screenlot-add-current-label" className="col-md-3">
          Current
        </div>
        <div className="col-md-3 padding0">
          <input
            id="screenlot-add-current-input"
            className="inputFieldDetails"
            onChange={onCurVolChange}
            value={lot?.currentVol}
            disabled={!canModify || isDefaultLot}
            type="number"
          />
        </div>
      </div>

      <div
        id="screenlot-add-submit-row"
        className="row plateRowColMargin my-[10px] mx-[30px] justify-end"
      >
        <button
          id="screenlot-add-submit-btn"
          onClick={onClick}
          className="success-button"
          disabled={!canModify}
        >
          {isNullOrUndefined(selectedLot) ? 'Add' : 'Update'}
        </button>
      </div>
    </div>
  );
};
export default memo(AddScreenLot);
const getNewScreenLot = (containers: Containers[]) => {
  if (!containers?.length) {
    return null;
  }
  const lot: IExperimentScreenLot = {
    id: getNewId(),
    comment: '',
    currentVol: 0,
    containerId: containers[0].id,
    dispenseVol: containers[0].defaultWellVolume,
    lotNumber: undefined,
    state: undefined,
  };
  return lot;
};
